import React, { useState, useRef, useEffect } from 'react';
import { FileText, Shield, CheckCircle, AlertCircle, Download, Printer, ChevronDown, ChevronRight, Sun, Moon, ArrowLeft, Lock } from 'lucide-react';
import { colors, LYT_INFO, URLS } from '../config/constants';
import { getTheme, useMobile } from '../utils/theme';

function NdaRateCard({ darkMode, setDarkMode, setCurrentPage, onboardingContext }) {
  const [step, setStep] = useState('info'); // info, sign, rates
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    title: '',
  });
  const [signature, setSignature] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [collapsed, setCollapsed] = useState({});

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const isMobile = useMobile();
  const theme = getTheme(darkMode);
  const { bgAlt, cardBg, text: textColor, textMedium: mutedColor, border, accentMarketing, logoLY, logoT, topBarBg, topBarText } = theme.colors;

  const accentColor = darkMode ? '#ff6b35' : '#0077B6';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  const config = onboardingContext;
  const rates = config?.rates || [];

  // Group rates by category
  const categories = {};
  rates.forEach(r => {
    if (!categories[r.category]) categories[r.category] = [];
    categories[r.category].push(r);
  });

  // Initialize canvas when step changes to sign
  useEffect(() => {
    if (step === 'sign' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    setStep('sign');
  };

  // Signature pad functions
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setHasSignature(true);
      const canvas = canvasRef.current;
      setSignature(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setSignature(null);
  };

  const handleNdaSubmit = async () => {
    if (!signature || !agreedToTerms) {
      setSubmitError('Please sign the document and agree to the terms.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        action: 'submitNDA',
        data: {
          ...formData,
          signature,
          signedDate: new Date().toISOString(),
          ipAddress: 'captured-server-side',
          userAgent: navigator.userAgent,
          source: 'nda-rate-card',
          project: config?.project || '',
        },
      };
      await fetch(URLS.appsScript, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setStep('rates');
    } catch (error) {
      console.error('NDA submission error:', error);
      setSubmitError('Failed to submit. Please try again or contact info@lytcomm.com');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCategory = (cat) => {
    setCollapsed(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const downloadCSV = () => {
    const header = 'Code,Description,UOM,Price,Category\n';
    const rows = rates.map(r => `"${r.code}","${r.description}","${r.uom}","${r.price}","${r.category}"`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `LYT_${(config?.project || 'Rate_Card').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inputStyle = {
    width: '100%',
    padding: isMobile ? '16px' : '12px 16px',
    fontSize: '16px',
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#1f2937',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: textColor,
    fontSize: '0.9rem',
  };

  const stepLabels = ['Your Info', 'Sign NDA', 'Rate Card'];
  const stepKeys = ['info', 'sign', 'rates'];
  const currentIdx = stepKeys.indexOf(step);

  // No config = bad route
  if (!config) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: textColor }}>
          <AlertCircle size={48} color={accentColor} style={{ marginBottom: 16 }} />
          <h2>Invalid Rate Card Link</h2>
          <p style={{ color: mutedColor }}>This code doesn't match any available rate card.</p>
          <button onClick={() => setCurrentPage('onboarding')} style={{ marginTop: 16, padding: '12px 24px', backgroundColor: accentColor, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            Back to Onboarding
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgAlt }}>
      {/* Top bar */}
      <div style={{ backgroundColor: topBarBg, padding: isMobile ? '8px 16px' : '6px 20px', display: 'flex', justifyContent: 'flex-end' }}>
        {setDarkMode && (
          <button onClick={() => setDarkMode(!darkMode)} style={{ backgroundColor: 'transparent', border: 'none', color: topBarText, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', padding: '8px 12px', borderRadius: '6px', minHeight: '44px' }}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Header */}
      <header style={{ padding: isMobile ? '12px 16px' : '16px 20px', backgroundColor: darkMode ? '#0d1b2a' : '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
        <button onClick={() => setCurrentPage('onboarding')} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'transparent', border: 'none', color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b', fontSize: isMobile ? '0.9rem' : '1rem', cursor: 'pointer', padding: '10px 12px', borderRadius: '8px', minHeight: '44px' }}>
          <ArrowLeft size={isMobile ? 18 : 20} /> {!isMobile && 'Back'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Shield size={isMobile ? 20 : 24} color={accentColor} />
          <span style={{ fontSize: isMobile ? '1rem' : '1.15rem', fontWeight: '700', color: textColor }}>NDA & Rate Card</span>
        </div>
        <button onClick={() => setCurrentPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', minHeight: '44px', minWidth: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700' }}>
            <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          </div>
        </button>
      </header>

      {/* Progress Steps */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: isMobile ? '20px 16px 0' : '28px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginBottom: isMobile ? '20px' : '28px' }}>
          {stepLabels.map((label, idx) => (
            <React.Fragment key={label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  backgroundColor: idx <= currentIdx ? accentColor : (darkMode ? '#374151' : '#e5e7eb'),
                  color: idx <= currentIdx ? '#fff' : mutedColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '600', fontSize: '0.85rem',
                }}>
                  {idx < currentIdx ? '\u2713' : idx + 1}
                </div>
                <span style={{ color: idx <= currentIdx ? textColor : mutedColor, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{label}</span>
              </div>
              {idx < 2 && (
                <div style={{ width: isMobile ? '24px' : '40px', height: '2px', backgroundColor: idx < currentIdx ? accentColor : (darkMode ? '#374151' : '#e5e7eb'), margin: '0 4px' }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: isMobile ? '0 16px 32px' : '0 20px 40px' }}>
        <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: isMobile ? '24px 20px' : '32px', boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)' }}>

          {/* ── Step 1: Info ── */}
          {step === 'info' && (
            <form onSubmit={handleInfoSubmit}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <FileText size={48} color={accentColor} style={{ marginBottom: '16px' }} />
                <h2 style={{ color: textColor, marginBottom: '8px' }}>Your Information</h2>
                <p style={{ color: mutedColor, fontSize: '0.9rem' }}>
                  Before viewing the <strong>{config.project}</strong> rate card, please provide your details and sign our NDA.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
                <div style={{ gridColumn: isMobile ? undefined : '1 / -1' }}>
                  <label style={labelStyle}>Full Name *</label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} style={inputStyle} required />
                </div>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} style={inputStyle} required />
                </div>
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} style={inputStyle} required />
                </div>
                <div style={{ gridColumn: isMobile ? undefined : '1 / -1' }}>
                  <label style={labelStyle}>Title / Position *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} style={inputStyle} required />
                </div>
              </div>

              <button type="submit" style={{
                width: '100%', marginTop: '28px', padding: isMobile ? '16px' : '14px',
                backgroundColor: accentColor, color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '1rem', fontWeight: '600', cursor: 'pointer', minHeight: '50px',
              }}>
                Continue to NDA
              </button>
            </form>
          )}

          {/* ── Step 2: Sign NDA ── */}
          {step === 'sign' && (
            <div>
              <h2 style={{ color: textColor, marginBottom: '8px' }}>Review & Sign NDA</h2>
              <p style={{ color: mutedColor, marginBottom: '24px', fontSize: '0.9rem' }}>
                Please review the agreement carefully, then sign below to unlock the rate card.
              </p>

              {/* NDA Content */}
              <div style={{
                maxHeight: '400px', overflowY: 'auto', padding: '20px',
                backgroundColor: darkMode ? '#0f172a' : '#f9fafb', borderRadius: '8px',
                border: `1px solid ${borderColor}`, fontSize: '0.9rem', lineHeight: '1.7', color: textColor,
              }}>
                <h2 style={{ textAlign: 'center', marginBottom: '20px', color: accentColor }}>NON-DISCLOSURE AGREEMENT</h2>

                <p style={{ marginBottom: '16px' }}>
                  <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p style={{ marginBottom: '16px' }}>This Non-Disclosure Agreement ("Agreement") is entered into by and between:</p>
                <p style={{ marginBottom: '16px', paddingLeft: '20px' }}><strong>Disclosing Party:</strong> {LYT_INFO.name}, a Texas limited liability company ("LYT" or "Company")</p>
                <p style={{ marginBottom: '16px', paddingLeft: '20px' }}><strong>Receiving Party:</strong> {formData.companyName || '[Company Name]'}, represented by {formData.fullName || '[Individual Name]'} ("Recipient")</p>
                <p style={{ marginBottom: '16px' }}>Collectively referred to as the "Parties."</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>1. PURPOSE</h3>
                <p style={{ marginBottom: '16px' }}>The Parties wish to explore a potential business relationship regarding fiber optic construction, telecommunications infrastructure, and related services. In connection with this opportunity, LYT may disclose certain confidential and proprietary information to Recipient.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>2. DEFINITION OF CONFIDENTIAL INFORMATION</h3>
                <p style={{ marginBottom: '16px' }}>"Confidential Information" means any and all non-public information disclosed by LYT to Recipient, whether orally, in writing, or by any other means, including but not limited to:</p>
                <ul style={{ marginBottom: '16px', paddingLeft: '30px' }}>
                  <li>Project specifications, scope of work documents, and bid packages</li>
                  <li>Customer and client lists, contact information, and business relationships</li>
                  <li>Pricing structures, rate cards, cost estimates, and financial data</li>
                  <li>Technical drawings, site plans, as-built documentation, and engineering data</li>
                  <li>Business strategies, operational methods, and competitive analysis</li>
                  <li>Employee and contractor information</li>
                  <li>Trade secrets, proprietary processes, and specialized techniques</li>
                  <li>Any information marked as "Confidential" or that a reasonable person would understand to be confidential</li>
                </ul>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>3. OBLIGATIONS OF RECIPIENT</h3>
                <p style={{ marginBottom: '16px' }}>Recipient agrees to:</p>
                <ul style={{ marginBottom: '16px', paddingLeft: '30px' }}>
                  <li>Hold all Confidential Information in strict confidence</li>
                  <li>Not disclose Confidential Information to any third party without LYT's prior written consent</li>
                  <li>Use Confidential Information solely for evaluating and engaging in business with LYT</li>
                  <li>Limit access to Confidential Information to employees and agents with a need to know</li>
                  <li>Protect Confidential Information using at least the same degree of care used to protect its own confidential information, but no less than reasonable care</li>
                  <li>Promptly notify LYT of any unauthorized use or disclosure</li>
                </ul>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>4. EXCLUSIONS</h3>
                <p style={{ marginBottom: '16px' }}>Confidential Information does not include information that:</p>
                <ul style={{ marginBottom: '16px', paddingLeft: '30px' }}>
                  <li>Is or becomes publicly available through no fault of Recipient</li>
                  <li>Was rightfully in Recipient's possession before disclosure by LYT</li>
                  <li>Is independently developed by Recipient without use of Confidential Information</li>
                  <li>Is rightfully obtained from a third party without restriction</li>
                  <li>Is required to be disclosed by law, provided Recipient gives LYT prompt notice</li>
                </ul>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>5. TERM AND TERMINATION</h3>
                <p style={{ marginBottom: '16px' }}>This Agreement shall remain in effect for a period of three (3) years from the Effective Date. The confidentiality obligations shall survive termination and continue for a period of five (5) years following any disclosure of Confidential Information.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>6. RETURN OF MATERIALS</h3>
                <p style={{ marginBottom: '16px' }}>Upon LYT's request or termination of discussions, Recipient shall promptly return or destroy all Confidential Information and any copies thereof, and certify such destruction in writing if requested.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>7. NO LICENSE OR WARRANTY</h3>
                <p style={{ marginBottom: '16px' }}>Nothing in this Agreement grants Recipient any rights to LYT's intellectual property. All Confidential Information is provided "AS IS" without warranty of any kind.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>8. REMEDIES</h3>
                <p style={{ marginBottom: '16px' }}>Recipient acknowledges that any breach of this Agreement may cause irreparable harm to LYT for which monetary damages would be inadequate. LYT shall be entitled to seek equitable relief, including injunction and specific performance, in addition to all other remedies available at law or in equity.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>9. GOVERNING LAW</h3>
                <p style={{ marginBottom: '16px' }}>This Agreement shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflicts of law principles. Any disputes arising under this Agreement shall be resolved in the state or federal courts located in Harris County, Texas.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>10. ENTIRE AGREEMENT</h3>
                <p style={{ marginBottom: '16px' }}>This Agreement constitutes the entire agreement between the Parties concerning confidentiality and supersedes all prior agreements and understandings. This Agreement may only be modified in writing signed by both Parties.</p>

                <h3 style={{ marginTop: '24px', marginBottom: '12px', color: accentColor }}>11. ELECTRONIC SIGNATURE</h3>
                <p style={{ marginBottom: '16px' }}>The Parties agree that this Agreement may be executed electronically and that electronic signatures shall have the same legal effect as original signatures under the Electronic Signatures in Global and National Commerce Act (ESIGN Act) and the Uniform Electronic Transactions Act (UETA).</p>

                <div style={{ marginTop: '32px', padding: '16px', backgroundColor: darkMode ? '#1e293b' : '#f0f9ff', borderRadius: '8px' }}>
                  <p style={{ fontWeight: '600', marginBottom: '8px' }}>DISCLOSING PARTY:</p>
                  <p>{LYT_INFO.name}</p>
                  <p style={{ fontStyle: 'italic', color: mutedColor, marginTop: '8px' }}>[Authorized signature on file]</p>
                </div>
              </div>

              {/* Signature Section */}
              <div style={{ marginTop: '32px' }}>
                <h3 style={{ color: textColor, marginBottom: '16px' }}>Recipient Signature</h3>
                <div style={{ backgroundColor: darkMode ? '#0f172a' : '#f9fafb', padding: '20px', borderRadius: '8px', border: `1px solid ${borderColor}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <span style={{ fontSize: '0.85rem', color: mutedColor }}>Name:</span>
                      <p style={{ color: textColor, fontWeight: '500', margin: '4px 0 0' }}>{formData.fullName}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.85rem', color: mutedColor }}>Title:</span>
                      <p style={{ color: textColor, fontWeight: '500', margin: '4px 0 0' }}>{formData.title}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.85rem', color: mutedColor }}>Company:</span>
                      <p style={{ color: textColor, fontWeight: '500', margin: '4px 0 0' }}>{formData.companyName}</p>
                    </div>
                    <div>
                      <span style={{ fontSize: '0.85rem', color: mutedColor }}>Date:</span>
                      <p style={{ color: textColor, fontWeight: '500', margin: '4px 0 0' }}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>

                  <label style={{ ...labelStyle, marginBottom: '8px' }}>Draw Your Signature Below *</label>
                  <div style={{ border: `2px solid ${hasSignature ? colors.green : borderColor}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      style={{ width: '100%', height: '150px', cursor: 'crosshair', touchAction: 'none' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <span style={{ fontSize: '0.85rem', color: hasSignature ? colors.green : mutedColor }}>
                      {hasSignature ? '\u2713 Signature captured' : 'Draw your signature above'}
                    </span>
                    <button type="button" onClick={clearSignature} style={{ padding: '6px 12px', fontSize: '0.85rem', backgroundColor: darkMode ? '#374151' : '#f3f4f6', border: `1px solid ${borderColor}`, borderRadius: '4px', cursor: 'pointer', color: textColor }}>
                      Clear
                    </button>
                  </div>
                </div>

                {/* Agreement Checkbox */}
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '24px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={{ marginTop: '4px', width: '18px', height: '18px' }} />
                  <span style={{ color: textColor, fontSize: '0.95rem', lineHeight: '1.5' }}>
                    I have read and understand this Non-Disclosure Agreement. I agree to be bound by its terms and conditions. I confirm that I have the authority to sign this agreement on behalf of {formData.companyName || 'my company'}.
                  </span>
                </label>

                {submitError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.coral, marginTop: '16px', padding: '12px', backgroundColor: darkMode ? 'rgba(232,90,79,0.1)' : 'rgba(232,90,79,0.05)', borderRadius: '8px' }}>
                    <AlertCircle size={18} />
                    {submitError}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                <button type="button" onClick={() => setStep('info')} style={{ padding: '14px 24px', backgroundColor: 'transparent', color: textColor, border: `1px solid ${borderColor}`, borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' }}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNdaSubmit}
                  disabled={!hasSignature || !agreedToTerms || submitting}
                  style={{
                    flex: 1, padding: '14px',
                    backgroundColor: (hasSignature && agreedToTerms && !submitting) ? accentColor : (darkMode ? '#374151' : '#d1d5db'),
                    color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600',
                    cursor: (hasSignature && agreedToTerms && !submitting) ? 'pointer' : 'not-allowed',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Sign NDA & View Rates'}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Rate Card ── */}
          {step === 'rates' && (
            <div>
              {/* Success banner */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: darkMode ? 'rgba(57,181,74,0.1)' : '#f0fdf4', borderRadius: '10px', marginBottom: '24px', border: `1px solid ${darkMode ? 'rgba(57,181,74,0.3)' : '#bbf7d0'}` }}>
                <CheckCircle size={24} color={colors.green} />
                <div>
                  <p style={{ color: textColor, fontWeight: '600', margin: 0 }}>NDA Signed Successfully</p>
                  <p style={{ color: mutedColor, fontSize: '0.85rem', margin: '2px 0 0' }}>A copy has been sent to {formData.email}. Reference: NDA-{Date.now().toString(36).toUpperCase()}</p>
                </div>
              </div>

              {/* Rate card title */}
              <h2 style={{ color: textColor, margin: '0 0 4px', fontSize: isMobile ? '1.3rem' : '1.5rem', fontWeight: 700 }}>
                {config.project} — Sub Rate Card
              </h2>
              <p style={{ color: mutedColor, margin: '0 0 4px', fontSize: '0.9rem' }}>
                <strong>Client:</strong> {config.client} &nbsp;|&nbsp; <strong>Payment Terms:</strong> {config.paymentTerms}
              </p>
              {config.retainageTerms && (
                <p style={{ color: mutedColor, margin: '0 0 16px', fontSize: '0.85rem' }}>
                  <strong>Retainage:</strong> {config.retainageTerms}
                </p>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
                <button onClick={downloadCSV} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: accentMarketing, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>
                  <Download size={16} /> Download CSV
                </button>
                <button onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 18px', backgroundColor: 'transparent', color: accentMarketing, border: `1px solid ${accentMarketing}`, borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>
                  <Printer size={16} /> Print / PDF
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Rate table (outside the card for full width) — only in rates step */}
        {step === 'rates' && Object.entries(categories).map(([category, items]) => {
          const isCollapsed = collapsed[category];
          return (
            <div key={category} style={{ backgroundColor: cardBg, borderRadius: '12px', marginTop: '12px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <button
                onClick={() => toggleCategory(category)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '14px 16px' : '16px 24px', backgroundColor: darkMode ? '#112240' : '#f1f5f9', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isCollapsed ? <ChevronRight size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
                  <span style={{ fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 700, color: textColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{category}</span>
                  <span style={{ fontSize: '0.8rem', color: mutedColor, fontWeight: 400 }}>({items.length} items)</span>
                </div>
              </button>
              {!isCollapsed && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.8rem' : '0.88rem' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${border}` }}>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'left', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Code</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'left', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Description</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'center', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>UOM</th>
                        <th style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'right', color: mutedColor, fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase' }}>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={item.code} style={{ borderBottom: idx < items.length - 1 ? `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}` : 'none' }}>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 24px', color: accentMarketing, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: isMobile ? '0.75rem' : '0.82rem' }}>{item.code}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 16px', color: textColor, lineHeight: 1.4 }}>{item.description}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 16px', textAlign: 'center', color: mutedColor, fontWeight: 500 }}>{item.uom}</td>
                          <td style={{ padding: isMobile ? '10px 12px' : '10px 24px', textAlign: 'right', color: textColor, fontWeight: 700, whiteSpace: 'nowrap' }}>{item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* Footer note — only in rates step */}
        {step === 'rates' && (
          <div style={{ textAlign: 'center', marginTop: '24px', padding: '16px', color: mutedColor, fontSize: '0.8rem' }}>
            <p style={{ margin: '0 0 4px' }}>Rates effective 2026. All prices are per-unit subcontractor rates.</p>
            <p style={{ margin: 0 }}>Questions? Contact <a href="mailto:matt@lytcomm.com" style={{ color: accentMarketing }}>matt@lytcomm.com</a></p>
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default NdaRateCard;
